// ─── Symbol Note Modal ───────────────────────────────────────
// A lightweight note editor for a single symbol. Opens in read mode,
// has an "Edit" button to switch to an editable textarea, and auto-saves
// when the user closes the modal (close button, outside click, or Escape).
// Optimized for both desktop and mobile.

import { useEffect, useRef, useState } from 'react';

interface SymbolNoteModalProps {
  symbolName: string;
  note: string;
  onSave: (note: string) => void;
  onClose: () => void;
}

export default function SymbolNoteModal({
  symbolName,
  note,
  onSave,
  onClose,
}: SymbolNoteModalProps) {
  const [draft, setDraft] = useState(note);
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Commit (save) and close only if the note actually changed.
  const commitAndClose = () => {
    if (draft.trim() !== note.trim()) {
      onSave(draft);
    }
    onClose();
  };

  // Reset: clear the note content and persist it as empty (null).
  const handleReset = () => {
    setDraft('');
    setIsEditing(false);
    onSave('');
  };

  // When entering edit mode, focus the textarea and place the cursor at the end.
  useEffect(() => {
    if (isEditing) {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }
  }, [isEditing]);

  // Close on Escape (also auto-saves).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        commitAndClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, note]);

  // Lock body scroll while the modal is open (prevents background jiggle on mobile).
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const hasNote = note.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) commitAndClose();
      }}
    >
      <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-5 py-3.5 border-b border-background-200/70 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-accent-400 text-base"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-heading font-semibold text-foreground-900 truncate">
              {symbolName}
            </h3>
            <p className="text-[10px] text-foreground-400">Quick note for this symbol</p>
          </div>
          <button
            type="button"
            onClick={commitAndClose}
            aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-200/70 hover:text-foreground-600 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-5">
          {isEditing ? (
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={6}
              placeholder="Type a note for this symbol..."
              className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 resize-none focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 placeholder:text-foreground-300"
            />
          ) : (
            <div
              className="min-h-[120px] px-3 py-2.5 rounded-lg bg-background-100/70 border border-background-200/70"
            >
              {hasNote ? (
                <p className="text-sm text-foreground-800 whitespace-pre-wrap break-words leading-relaxed">
                  {note}
                </p>
              ) : (
                <p className="text-sm text-foreground-300 italic">
                  No note yet. Click "Edit" to add one.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-3.5 border-t border-background-200/70 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-restart-line mr-1"></i>
            Reset
          </button>
          {isEditing ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setDraft(note);
                  setIsEditing(false);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-500 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={commitAndClose}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line mr-1"></i>
                Save
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-secondary-500 text-background-50 dark:text-foreground-950 hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line mr-1"></i>
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}